import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, shareReplay, tap } from 'rxjs';
import { GenericService } from './generic.service';
import { EstacaoLookupCacheService } from './estacao-lookup-cache.service';

export type EstacaoDownloadListaPayload = {
  search?: string;
  grupoList?: number[];
  clienteList?: number[];
  sensorList?: number[];
  plu?: string | null;
  flu?: string | null;
  integradorList?: number[];
  coletorList?: number[];
  estacoes_ids?: number[];
};

export type EstacaoDownloadListaEstacao = {
  id?: number;
  descricao?: string | null;
  cliente_id?: number | null;
  cliente_nome?: string | null;
  transmissor_identificador?: string | null;
  coletor_identificador?: string | null;
  integrador_identificador?: string | null;
  plu?: string | number | null;
  flu?: string | number | null;
  localizacao?: string | null;
  latitude?: string | number | null;
  longitude?: string | number | null;
  data_instalacao?: string | null;
  status?: number | string | null;
};

export type EstacaoDownloadListaResponse = {
  estacoes: EstacaoDownloadListaEstacao[];
  total: number;
};

export type ConfiguracaoVazaoHistoricoFaixa = {
  id: number;
  intervalo_inical: number;
  intervalo_final: number;
  valor_x: number;
  valor_y: number;
  valor_z: number;
};

export type ConfiguracaoVazaoHistorico = {
  id: number;
  estacao_id: number;
  ativo: boolean;
  tipo_origem_vazao: number | null;
  casas_decimais_vazao: number | null;
  usuario_id: number | null;
  reutilizada_de_id: number | null;
  created_at: string;
  usuario?: { id: number; name: string } | null;
  faixas: ConfiguracaoVazaoHistoricoFaixa[];
};

export type ConfiguracaoVazaoHistoricoResponse = {
  data: ConfiguracaoVazaoHistorico[];
  current_page: number;
  per_page: number;
  total: number;
};

@Injectable({
  providedIn: 'root'
})
export class EstacaoService extends GenericService {
  private lookup$?: Observable<any[]>;

  private buildListGridUrl(params: {
    page?: number,
    per_page?: number,
    search?: string,
    grupoList?: any[],
    estacoes_ids?: any[],
    clienteList?: any[],
    sensorList?: any[],
    plu?: string | null,
    flu?: string | null,
    integradorList?: any[],
    coletorList?: any[],
  } = {}): string {
    let url = `${this.url_base}/${this.prefixo}/list/grid`;
    const query: string[] = [];

    if (params.page) {
      query.push(`page=${params.page}`);
    }
    if (params.per_page) {
      query.push(`per_page=${params.per_page}`);
    }
    if (params.search) {
      query.push(`search=${encodeURIComponent(params.search)}`);
    }
    (params.grupoList || []).forEach(element => query.push(`grupoList[]=${element}`));
    (params.estacoes_ids || []).forEach(element => query.push(`estacoes_ids[]=${element}`));
    (params.clienteList || []).forEach(element => query.push(`clienteList[]=${element}`));
    (params.sensorList || []).forEach(element => query.push(`sensorList[]=${element}`));
    (params.integradorList || []).forEach(element => query.push(`integradorList[]=${element}`));
    (params.coletorList || []).forEach(element => query.push(`coletorList[]=${element}`));

    if (params.plu != null && params.plu !== '') {
      query.push(`plu=${encodeURIComponent(params.plu)}`);
    }
    if (params.flu != null && params.flu !== '') {
      query.push(`flu=${encodeURIComponent(params.flu)}`);
    }

    if (query.length > 0) {
      url += `?${query.join('&')}`;
    }

    return url;
  }

  constructor(http: HttpClient, private lookupSessionCache: EstacaoLookupCacheService) {
    super(http, 'estacoes');
  }

  lookup(): Observable<any[]> {
    if (!this.lookup$) {
      this.lookup$ = this.http.get<any[]>(`${this.url_base}/${this.prefixo}/lookup`).pipe(
        tap({
          error: () => {
            this.lookup$ = undefined;
          }
        }),
        shareReplay(1)
      );
    }
    return this.lookup$;
  }

  invalidateLookupCache(): void {
    this.lookup$ = undefined;
    this.lookupSessionCache.delete('estacoes-filtro-exportacao');
  }

  override store(params: any): Observable<any> {
    return super.store(params).pipe(tap(() => this.invalidateLookupCache()));
  }

  override update(params: any, id: number): Observable<any> {
    return super.update(params, id).pipe(tap(() => this.invalidateLookupCache()));
  }

  override destroy(id: number): Observable<any> {
    return super.destroy(id).pipe(tap(() => this.invalidateLookupCache()));
  }

  lastEvents(estacao): Observable<any> {
    return this.http.get(`${this.url_base}/${this.prefixo}/${estacao}/ultimos-eventos`);
  }

  getStatus(estacao, dia, modoMapaExterno = false): Observable<any> {
    const query = modoMapaExterno ? '?modo_mapa_externo=1' : '';
    return this.http.get(`${this.url_base}/${this.prefixo}/status/${estacao}/${dia}${query}`);
  }

  getMonitoramentos(estacao, dia): Observable<any> {
    return this.http.get(`${this.url_base}/${this.prefixo}/monitoramentos/${estacao}/${dia}`);
  }

  formData(id: number): Observable<any> {
    return this.http.get(`${this.url_base}/${this.prefixo}/${id}/form-data`);
  }

  integracaoLogs(id: number, page = 1): Observable<any> {
    return this.http.get(`${this.url_base}/${this.prefixo}/${id}/integracao-logs?page=${page}`);
  }

  historicoConfiguracoesVazao(id: number, page = 1, perPage = 10): Observable<ConfiguracaoVazaoHistoricoResponse> {
    return this.http.get<ConfiguracaoVazaoHistoricoResponse>(
      `${this.url_base}/${this.prefixo}/${id}/configuracoes-vazao/historico?page=${page}&per_page=${perPage}`
    );
  }

  listGrid(params: {
    page?: number,
    per_page?: number,
    search?: string,
    grupoList?: any[],
    estacoes_ids?: any[],
    clienteList?: any[],
    sensorList?: any[],
    plu?: string | null,
    flu?: string | null,
    integradorList?: any[],
    coletorList?: any[],
  } = {}): Observable<any> {
    return this.http.get(this.buildListGridUrl(params));
  }

  downloadLista(payload: EstacaoDownloadListaPayload): Observable<EstacaoDownloadListaResponse> {
    return this.http.post<EstacaoDownloadListaResponse>(`${this.url_base}/${this.prefixo}/download-lista`, payload);
  }

  filter(grupoList = [], clienteList = [], sensorList = [], plu = null, flu = null, integradorList= [], coletorList = []): Observable<any> {
    let url = `${this.url_base}/${this.prefixo}`;

    let query: string = '';

    
          if (grupoList.length > 0) {
            grupoList.forEach(element => {
              query += `&grupoList[]=${element}`;
            });
          }
          if (clienteList.length > 0) {
            clienteList.forEach(element => {
              query += `&clienteList[]=${element}`;
            });
          }
          if (sensorList.length > 0) {
            sensorList.forEach(element => {
              query += `&sensorList[]=${element}`;
            });
          }

          if(plu != null && plu !== ''){
            query += `&plu=${plu}`;
          }

          if(flu != null && flu !== ''){
            query += `&flu=${flu}`;
          }

          if (integradorList.length > 0) {
            integradorList.forEach(element => {
              query += `&integradorList[]=${element}`;
            });
          }

          if (coletorList.length > 0) {
            coletorList.forEach(element => {
              query += `&coletorList[]=${element}`;
            });
          }

          if (query) {
            url += `?${query.substring(1)}`;
          }

        

    return this.http.get(url);
  }

  excluir(id: any): Observable<any> {
    return this.http.delete(`${this.url_base}/${this.prefixo}/${id}`).pipe(
      tap(() => this.invalidateLookupCache())
    );
  }

  /**
   * Obtém usuário e senha (descriptografados) da configuração de integração da estação.
   * Apenas para usuário interno.
   */
  getIntegracaoCredenciais(estacaoId: number, configuracaoIntegracaoEstacaoId: number): Observable<any> {
    return this.http.get(`${this.url_base}/${this.prefixo}/${estacaoId}/integracao-credenciais/${configuracaoIntegracaoEstacaoId}`);
  }

  getSnirhSerieHistorica(codigo: string | number, tipo: 'PLU' | 'FLU'): Observable<any> {
    const codigoParam = encodeURIComponent(String(codigo ?? '').trim());
    const tipoParam = encodeURIComponent(tipo);

    return this.http.get(`${this.url_base}/${this.prefixo}/snirh/serie-historica?codigo=${codigoParam}&tipo=${tipoParam}`);
  }

  listReprocessamentos(estacaoId: number): Observable<any> {
    return this.http.get(`${this.url_base}/${this.prefixo}/${estacaoId}/reprocessamentos`);
  }
}
